import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useGlobalLocation } from '@/contexts/LocationContext';
import { KpiCard } from '@/components/ui/KpiCard';
import { WeatherWidget } from '@/components/ui/WeatherWidget';
import { Loader2, CalendarDays, Users, ClipboardList, Briefcase, FileSignature, Pill, Clock, BriefcaseBusiness, Activity } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

import { useAuth } from '@/contexts/AuthContext';
const STATUS_META = {
  green: { label: 'Volledig', dot: 'bg-emerald-500', ring: 'ring-emerald-200', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  yellow: { label: 'Aandacht', dot: 'bg-amber-500', ring: 'ring-amber-200', text: 'text-amber-700', bg: 'bg-amber-50' },
  red: { label: 'Krap', dot: 'bg-rose-500', ring: 'ring-rose-200', text: 'text-rose-700', bg: 'bg-rose-50' },
  gray: { label: 'Geen data', dot: 'bg-slate-300', ring: 'ring-slate-200', text: 'text-slate-500', bg: 'bg-slate-50' },
};

export default function LocationCockpit() {
  const { user } = useAuth();
  const { slug } = useParams();
  const navigate = useNavigate();
  const { setGlobalLocation } = useGlobalLocation();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ziekCount, setZiekCount] = useState(null);
  const [teLaat, setTeLaat] = useState(null);
  const [guardCount, setGuardCount] = useState(null);
  const [budget, setBudget] = useState(null);
  const [liveCost, setLiveCost] = useState(null);
  const [weekPk, setWeekPk] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [prestatie, setPrestatie] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [briefingBusy, setBriefingBusy] = useState(false);
  const [omzetInput, setOmzetInput] = useState('');
  const canEditOmzet = ['owner', 'admin', 'multi_location'].includes(user?.role);
  const todayIso = new Date().toISOString().slice(0, 10);

  // Forecast-nauwkeurigheid (item 2): rolling ±% over de laatste 4 afgeronde weken.
  // Onafhankelijke fetch (kan even duren bij de allereerste berekening; daarna gecachet).
  useEffect(() => {
    let cancelled = false;
    if (!slug) return;
    setAccuracy(null);
    api.get('/dailyops/accuracy', { params: { location_id: slug } })
      .then(({ data }) => { if (!cancelled) setAccuracy(data); })
      .catch(() => { if (!cancelled) setAccuracy({ status: 'onvoldoende', avg_abs_deviation_pct: null }); });
    return () => { cancelled = true; };
  }, [slug]);

  // Locatie-specifieke AI-weekbriefing (on-demand + 6h-cache per locatie).
  const loadBriefing = React.useCallback(async (refresh = false) => {
    if (!slug) return;
    if (refresh) setBriefingBusy(true);
    try {
      const { data: bd } = await api.get('/dashboard/location-briefing', {
        params: { location_id: slug, refresh },
      });
      setBriefing(bd.briefing);
    } catch (e) {
      setBriefing({ briefing_text: 'Briefing tijdelijk niet beschikbaar.', from_cache: false, usage: { model: 'fallback' } });
    } finally {
      setBriefingBusy(false);
    }
  }, [slug]);

  useEffect(() => {
    setBriefing(null);
    loadBriefing(false);
  }, [loadBriefing]);

  // DailyOps-prestatie-strip (item 4): read-only KPI's van deze locatie (vandaag + week).
  useEffect(() => {
    let cancelled = false;
    if (!slug) return;
    setPrestatie(null);
    api.get('/dailyops/prestatie', { params: { location_id: slug } })
      .then(({ data }) => { if (!cancelled) setPrestatie(data); })
      .catch(() => { if (!cancelled) setPrestatie({ available: false }); });
    return () => { cancelled = true; };
  }, [slug]);

  // Ensure global scope syncs with current URL params
  useEffect(() => {
    if (slug) {
      setGlobalLocation(slug);
    }
  }, [slug, setGlobalLocation]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    
    // We reuse /dashboard/full for the main numbers
    api.get('/dashboard/full').then(res => {
      if (!cancelled) {
        const fullData = res.data;
        const locStaffing = fullData.staffing.find(s => s.slug === slug);
        
        if (!locStaffing) {
          // Fallback if not found (e.g. invalid slug or no access)
          navigate('/locaties', { replace: true });
          return;
        }

        setData({
          iso_week: fullData.iso_week,
          iso_year: fullData.iso_year,
          locData: locStaffing,
          // Locatie-briefing wordt apart opgehaald (eigen useEffect); dashboard behoudt de groepsbrede.
          // approvals total is globally loaded, but we don't have per-location specific via this endpoint without extra call.
          // Will render a navigational tile as instructed.
        });
        setLoading(false);
      }
    }).catch(err => {
      console.error('Cockpit load failed', err);
      if (!cancelled) {
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [slug, navigate]);

  // Ziek vandaag — real operational verzuim count for this location.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setZiekCount(null);
    api.get('/verzuim/active', { params: { location: slug } })
      .then((res) => { if (!cancelled) setZiekCount(res.data.count ?? 0); })
      .catch(() => { if (!cancelled) setZiekCount(null); });
    return () => { cancelled = true; };
  }, [slug]);

  // Te laat geklokt — real count for today (read-only, location-scoped).
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setTeLaat(null);
    api.get(`/clock/${slug}/te-laat`)
      .then((res) => { if (!cancelled) setTeLaat(res.data); })
      .catch(() => { if (!cancelled) setTeLaat(null); });
    return () => { cancelled = true; };
  }, [slug]);

  // Roster-guard — out-of-band count for the current week (N2).
  useEffect(() => {
    if (!slug || !data?.iso_year || !data?.iso_week) return;
    let cancelled = false;
    setGuardCount(null);
    api.get('/roster-guard', { params: { location_id: slug, year: data.iso_year, week: data.iso_week } })
      .then((res) => {
        if (cancelled) return;
        setGuardCount(res.data.enabled ? (res.data.summary?.total_flagged ?? 0) : null);
        setBudget(res.data.budget || null);
      })
      .catch(() => { if (!cancelled) { setGuardCount(null); setBudget(null); } });
    return () => { cancelled = true; };
  }, [slug, data?.iso_year, data?.iso_week]);

  // LIVE loonkosten-teller — indicative labor cost of TODAY (polls every 60s).
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLiveCost(null);
    const fetchLive = () => {
      api.get(`/loonkosten/cockpit/${slug}/live`)
        .then((res) => {
          if (cancelled) return;
          setLiveCost(res.data);
          if (res.data.expected_revenue_today != null) setOmzetInput(String(res.data.expected_revenue_today));
        })
        .catch(() => { if (!cancelled) setLiveCost(null); });
    };
    fetchLive();
    const t = setInterval(fetchLive, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [slug]);

  // Gepland PK% for the current week (loonkosten / verwachte omzet).
  useEffect(() => {
    if (!slug || !data?.iso_year || !data?.iso_week) return;
    let cancelled = false;
    api.get('/loonkosten/rooster', { params: { location_id: slug, year: data.iso_year, week: data.iso_week } })
      .then((res) => { if (!cancelled) setWeekPk(res.data.pk || null); })
      .catch(() => { if (!cancelled) setWeekPk(null); });
    return () => { cancelled = true; };
  }, [slug, data?.iso_year, data?.iso_week]);

  const saveOmzet = () => {
    const amount = omzetInput === '' ? null : Number(omzetInput);
    api.put('/omzet', { location_slug: slug, date: todayIso, amount })
      .then(() => api.get(`/loonkosten/cockpit/${slug}/live`))
      .then((res) => setLiveCost(res.data))
      .catch(() => {});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 size={20} className="animate-spin mr-2" /> Cockpit laden…
      </div>
    );
  }

  if (!data) return null;

  const { locData, iso_week, iso_year } = data;
  const meta = STATUS_META[locData.status] || STATUS_META.gray;

  const greetingTime = new Date().getHours();
  const greeting = greetingTime < 12 ? 'Goedemorgen' : greetingTime < 18 ? 'Goedemiddag' : 'Goedenavond';
  const firstName = user?.email?.split('@')[0]?.split('.')[0] || 'collega';
  const firstNameCap = firstName.charAt(0).toUpperCase() + firstName.slice(1);

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="page-cockpit">
      {/* Header */}
      <div>
        <div className="flex items-center text-xs text-tw1-text-muted mb-2 gap-2">
          <Link to="/locaties" className="hover:text-tw1-brand-navy hover:underline">Locaties</Link>
          <span>/</span>
          <span className="font-semibold text-slate-900">{locData.name}</span>
        </div>
        
        <div className="flex items-end justify-between gap-4 border-b border-[#e4e9f0] pb-4">
          <div>
            <h2 className="text-3xl font-bold text-tw1-brand-navy">{greeting}, {firstNameCap}</h2>
            <p className="mt-1 text-sm text-tw1-text-muted">
              Week {iso_week} / {iso_year} — cockpit voor {locData.name}.
            </p>
            <div className="flex items-center gap-3 mt-4">
              <Badge variant="outline" className={`${meta.bg} ${meta.text} border-transparent text-xs`}>
                <span className={`h-2 w-2 rounded-full ${meta.dot} mr-1.5 inline-block`} />
                Status: {meta.label}
              </Badge>
              <span className="text-sm text-tw1-text-muted">
                {locData.type === 'truckwash' ? 'Truckwash' : locData.type || 'Organisatorisch'} · {locData.employees_active} medewerkers
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Weather Widget */}
      <WeatherWidget locationSlug={slug} />

      {/* LIVE loonkosten-teller — indicative labor cost running today */}
      {liveCost && (() => {
        const PK_TONE = {
          gold: 'bg-yellow-100 text-yellow-800',
          green: 'bg-emerald-100 text-emerald-700',
          orange: 'bg-orange-100 text-orange-700',
          red: 'bg-rose-100 text-rose-700',
          unknown: 'bg-slate-100 text-slate-500',
        };
        const pkLive = liveCost.pk_live || {};
        return (
        <div
          className="rounded-lg border border-[#e4e9f0] bg-white p-4 shadow-sm border-l-[3px] border-l-tw1-brand-navy"
          data-testid="cockpit-live-cost"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-[38px] w-[38px] items-center justify-center rounded-lg bg-[#eef2f9] text-tw1-brand-navy">
                <Clock size={18} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-tw1-brand-navy">
                  Vandaag draait er nu <span className="tabular-nums" data-testid="cockpit-live-cost-amount">€{liveCost.total_eur.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> aan loon
                </h3>
                <p className="text-[12px] text-tw1-text-muted">
                  {liveCost.employee_count} medewerker(s) geklokt vandaag
                  {liveCost.open_count > 0 ? ` · ${liveCost.open_count} nog ingeklokt` : ''}
                  {liveCost.unpriced_count > 0 ? ` · ${liveCost.unpriced_count} zonder tarief` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {pkLive.pk_pct != null && (
                <Badge className={`${PK_TONE[pkLive.status] || PK_TONE.unknown} border-transparent text-[11px]`} data-testid="cockpit-live-pk">
                  PK% {pkLive.pk_pct}% (live)
                </Badge>
              )}
              <Badge variant="outline" className="bg-slate-50 text-slate-500 border-transparent text-[11px]" data-testid="cockpit-live-cost-badge">
                Live · indicatief
              </Badge>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3">
            <label className="text-[12px] text-tw1-text-muted">Verwachte omzet vandaag</label>
            {canEditOmzet ? (
              <>
                <div className="flex items-center gap-1">
                  <span className="text-slate-400 text-sm">€</span>
                  <input
                    type="number" min="0" step="1"
                    value={omzetInput}
                    onChange={(e) => setOmzetInput(e.target.value)}
                    onBlur={saveOmzet}
                    data-testid="cockpit-omzet-input"
                    className="w-32 rounded-md border border-slate-300 px-2 py-1 text-sm text-right"
                    placeholder="—"
                  />
                </div>
                {weekPk?.pk_pct != null && (
                  <span className="ml-auto text-[12px] text-tw1-text-muted" data-testid="cockpit-week-pk">
                    Gepland deze week: <span className={`rounded px-1.5 py-0.5 font-medium ${PK_TONE[weekPk.status] || PK_TONE.unknown}`}>PK% {weekPk.pk_pct}%</span>
                  </span>
                )}
              </>
            ) : (
              <span className="text-sm font-medium text-slate-700" data-testid="cockpit-omzet-readonly">
                {liveCost.expected_revenue_today != null ? `€${Number(liveCost.expected_revenue_today).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'niet ingevuld'}
              </span>
            )}
          </div>
        </div>
        );
      })()}

      {/* DailyOps-prestatie-strip (item 4) — dagelijkse motivator, read-only */}
      {prestatie && prestatie.available && (() => {
        const eur = (v) => v == null ? '—' : `€${Number(v).toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
        const pct = (v) => v == null ? '—' : `${v}%`;
        const sync = prestatie.week_sync || prestatie.today_sync || {};
        const stale = sync.source && sync.source !== 'live';
        const Kpi = ({ label, value, hint }) => (
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wide text-white/50">{label}</span>
            <span className="text-sm font-semibold text-white tabular-nums">{value}</span>
            {hint && <span className="text-[10px] text-tw1-accent-gold/90 tabular-nums">{hint}</span>}
          </div>
        );
        const Group = ({ title, k }) => (
          <div className="flex-1 min-w-[220px]">
            <div className="mb-1.5 text-[11px] font-semibold text-tw1-accent-gold">{title}</div>
            {k ? (
              <div className="grid grid-cols-4 gap-3">
                <Kpi label="Omzet" value={eur(k.omzet)} hint={k.pct_of_target != null ? `${k.pct_of_target}% v. doel` : null} />
                <Kpi label="PK%" value={pct(k.pk_pct)} />
                <Kpi label="Bijverkoop" value={pct(k.bijverkoop_pct)} />
                <Kpi label="Wassingen" value={k.wassingen ?? '—'} />
              </div>
            ) : <div className="text-[11px] text-white/40">Geen data</div>}
          </div>
        );
        return (
          <div className="rounded-lg border border-tw1-brand-navy/20 bg-tw1-brand-navy p-4 shadow-sm" data-testid="cockpit-dailyops-strip">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-tw1-accent-gold" />
                <span className="text-[13px] font-semibold text-white">Truckwash-prestatie</span>
                <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium text-white/70" data-testid="cockpit-dailyops-source">DailyOps</span>
              </div>
              <span className="text-[10px] text-white/50" data-testid="cockpit-dailyops-sync">
                {stale ? 'laatst bekend' : 'live'}{sync.fetched_at ? ` · ${new Date(sync.fetched_at).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}
              </span>
            </div>
            <div className="flex flex-wrap gap-6">
              <Group title="Vandaag" k={prestatie.today} />
              <div className="hidden sm:block w-px self-stretch bg-white/10" />
              <Group title="Deze week" k={prestatie.week} />
            </div>
          </div>
        );
      })()}

      {/* Forecast-nauwkeurigheid (item 2) — rolling ±% laatste 4 afgeronde weken */}
      {(() => {
        const ACC_TONE = {
          groen: { badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
          oranje: { badge: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
          rood: { badge: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500' },
          onvoldoende: { badge: 'bg-slate-100 text-slate-500', dot: 'bg-slate-300' },
        };
        if (accuracy === null) {
          return (
            <div className="flex items-center gap-2 rounded-lg border border-[#e4e9f0] bg-white p-3 text-[12px] text-tw1-text-muted" data-testid="cockpit-forecast-accuracy-loading">
              <Loader2 size={14} className="animate-spin" /> Forecast-nauwkeurigheid berekenen…
            </div>
          );
        }
        const st = accuracy.status || 'onvoldoende';
        const tone = ACC_TONE[st] || ACC_TONE.onvoldoende;
        const enough = st !== 'onvoldoende' && accuracy.avg_abs_deviation_pct != null;
        return (
          <div
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#e4e9f0] bg-white p-3 shadow-sm"
            data-testid="cockpit-forecast-accuracy"
            title="Hoe goed de omzet-schatting de laatste 4 afgeronde weken zat: het gemiddelde verschil tussen de geschatte en de werkelijke omzet. Lager = beter."
          >
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
              <span className="text-[13px] font-medium text-tw1-brand-navy">Forecast-nauwkeurigheid</span>
            </div>
            {enough ? (
              <span className={`rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${tone.badge}`} data-testid="cockpit-forecast-accuracy-value">
                ±{accuracy.avg_abs_deviation_pct}% (laatste {accuracy.weeks_used} {accuracy.weeks_used === 1 ? 'week' : 'weken'})
              </span>
            ) : (
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[12px] font-medium text-slate-500" data-testid="cockpit-forecast-accuracy-value">
                Nog onvoldoende historie
              </span>
            )}
          </div>
        );
      })()}

      {/* Uren-budget status (N2) — at-a-glance over/under for THIS week */}
      {budget && (
        budget.enabled ? (
          (() => {
            const over = budget.status === 'over';
            const under = budget.status === 'under';
            const tone = over
              ? { bg: 'bg-rose-50', border: 'border-l-tw1-status-danger', text: 'text-rose-700', badge: 'bg-rose-100 text-rose-700', label: 'Boven budget' }
              : under
                ? { bg: 'bg-amber-50', border: 'border-l-tw1-accent-gold', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', label: 'Onder budget' }
                : { bg: 'bg-emerald-50', border: 'border-l-emerald-500', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700', label: 'Binnen budget' };
            const rangeLabel = `${budget.min != null ? `${budget.min}u` : '—'} – ${budget.max != null ? `${budget.max}u` : '—'}`;
            return (
              <Link
                to={`/rooster?loc=${slug}`}
                data-testid="cockpit-budget-banner"
                className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#e4e9f0] ${tone.bg} p-4 shadow-sm border-l-[3px] ${tone.border} transition-all hover:shadow-md`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-[38px] w-[38px] items-center justify-center rounded-lg bg-white/70 text-tw1-brand-navy">
                    <Activity size={18} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-tw1-brand-navy">Uren-budget (week {iso_week})</h3>
                    <p className="text-[12px] text-tw1-text-muted">
                      Gepland <span className="font-semibold tabular-nums">{budget.total_planned}u</span> · budget {rangeLabel}
                      {budget.planned_cost_eur != null && (
                        <> · <span className="font-semibold tabular-nums" data-testid="cockpit-budget-cost">€{budget.planned_cost_eur.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> geplande loonkosten</>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {over && <span className={`text-[12px] font-medium ${tone.text}`} data-testid="cockpit-budget-delta">+{budget.over_by}u boven max</span>}
                  {under && <span className={`text-[12px] font-medium ${tone.text}`} data-testid="cockpit-budget-delta">{budget.under_by}u onder min</span>}
                  <Badge className={`${tone.badge} border-transparent text-[11px] hover:${tone.badge}`} data-testid="cockpit-budget-status">{tone.label}</Badge>
                </div>
              </Link>
            );
          })()
        ) : (
          <div
            className="rounded-lg border border-dashed border-[#e4e9f0] bg-white p-3 text-[12px] text-tw1-text-muted"
            data-testid="cockpit-budget-empty"
          >
            Geen uren-budget ingesteld voor deze locatie.
          </div>
        )
      )}


      {/* Locatie-specifieke AI Weekbriefing */}
      {briefing && (
        <div
          className="relative overflow-hidden rounded-lg border border-[#e4e9f0] bg-white p-5 shadow-sm border-l-[3px] border-l-tw1-accent-gold"
          data-testid="cockpit-ai-weekbriefing"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-3">
              <div className="flex h-[38px] w-[38px] items-center justify-center rounded-lg bg-[#fdf3da] text-[#9a6b16]">
                <Activity size={18} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-tw1-brand-navy">AI Weekbriefing — {data.locData?.name || slug}</h3>
                <p className="text-[10px] text-tw1-text-muted">
                  {briefing.from_cache ? `Gegenereerd ${new Date(briefing.generated_at).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}` : 'Net gegenereerd'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => loadBriefing(true)}
              disabled={briefingBusy}
              data-testid="cockpit-briefing-refresh"
              className="flex items-center gap-1 rounded-md border border-[#e4e9f0] bg-white px-2 py-1 text-[11px] font-medium text-tw1-brand-navy transition-colors hover:bg-slate-50 disabled:opacity-50"
            >
              {briefingBusy ? <Loader2 size={12} className="animate-spin" /> : <Activity size={12} />} Ververs
            </button>
          </div>
          <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-line" data-testid="cockpit-briefing-text">{briefing.briefing_text}</p>
          {briefing.stats && Object.keys(briefing.stats).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="cockpit-briefing-stats">
              {typeof briefing.stats.geplande_diensten === 'number' && (
                <span data-testid="cockpit-stat-diensten" className="inline-flex items-center gap-1 rounded-full border border-[#e4e9f0] bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-tw1-brand-navy">
                  Geplande diensten: <span className="font-semibold">{briefing.stats.geplande_diensten}</span>
                </span>
              )}
              {typeof briefing.stats.dagen_onderbezet === 'number' && (
                <span data-testid="cockpit-stat-onderbezet" className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium ${briefing.stats.dagen_onderbezet > 0 ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-[#e4e9f0] bg-slate-50 text-tw1-brand-navy'}`}>
                  Onderbezet: <span className="font-semibold">{briefing.stats.dagen_onderbezet} dg</span>
                </span>
              )}
              {typeof briefing.stats.dagen_overbezet === 'number' && (
                <span data-testid="cockpit-stat-overbezet" className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium ${briefing.stats.dagen_overbezet > 0 ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-[#e4e9f0] bg-slate-50 text-tw1-brand-navy'}`}>
                  Overbezet: <span className="font-semibold">{briefing.stats.dagen_overbezet} dg</span>
                </span>
              )}
              {typeof briefing.stats.forecast_omzet_week === 'number' && (
                <span data-testid="cockpit-stat-forecast" className="inline-flex items-center gap-1 rounded-full border border-[#e4e9f0] bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-tw1-brand-navy">
                  Forecast omzet: <span className="font-semibold">€{Number(briefing.stats.forecast_omzet_week).toLocaleString('nl-NL')}</span>
                </span>
              )}
              {typeof briefing.stats.pk_projectie_pct === 'number' && (
                <span data-testid="cockpit-stat-pk" className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium ${briefing.stats.pk_status === 'rood' ? 'border-rose-200 bg-rose-50 text-rose-700' : briefing.stats.pk_status === 'oranje' ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                  PK-projectie: <span className="font-semibold">{Number(briefing.stats.pk_projectie_pct).toFixed(1)}%</span>
                  {typeof briefing.stats.pk_doel_pct === 'number' && <span className="opacity-70">(doel {Number(briefing.stats.pk_doel_pct).toFixed(1)}%)</span>}
                </span>
              )}
              {typeof briefing.stats.verlof_ziek_deze_week === 'number' && (
                <span data-testid="cockpit-stat-verlof-ziek" className="inline-flex items-center gap-1 rounded-full border border-[#e4e9f0] bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-tw1-brand-navy">
                  Verlof/ziek: <span className="font-semibold">{briefing.stats.verlof_ziek_deze_week}</span>
                </span>
              )}
              {typeof briefing.stats.dailyops_omzet_week === 'number' && (
                <span data-testid="cockpit-stat-dailyops" className="inline-flex items-center gap-1 rounded-full border border-[#e4e9f0] bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-tw1-brand-navy">
                  DailyOps: <span className="font-semibold">€{Number(briefing.stats.dailyops_omzet_week).toLocaleString('nl-NL')}</span>
                  {typeof briefing.stats.dailyops_target_week === 'number' && <span className="opacity-70">/ €{Number(briefing.stats.dailyops_target_week).toLocaleString('nl-NL')}</span>}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Grid of 9 Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* 1. Bezetting */}
        <KpiCard
          icon={Activity}
          label="Bezetting (deze week)"
          value={locData.coverage_pct !== null ? `${locData.coverage_pct}%` : '—'}
          sublabel={`${locData.planned_hours}u / ${locData.contract_hours}u gepland`}
          type={locData.status === 'red' ? 'danger' : locData.status === 'yellow' ? 'attention' : 'neutral'}
          to={`/rooster`}
        />

        {/* 2. Rooster */}
        <KpiCard
          icon={CalendarDays}
          label="Rooster"
          value={`W${iso_week}`}
          sublabel="Roosterplanner openen"
          type="neutral"
          to={`/rooster`}
        />

        {/* 3. Personeel */}
        <KpiCard
          icon={Users}
          label="Personeel"
          value={locData.employees_active}
          sublabel="Actieve medewerkers"
          type="neutral"
          to={`/medewerkers`}
        />

        {/* 4. Uren goedkeuren (Navigational tile) */}
        <KpiCard
          icon={ClipboardList}
          label="Uren goedkeuren"
          value="Bekijken"
          sublabel="Uren-overzicht openen"
          type="neutral"
          to={`/uren-overzicht`}
        />

        {/* 5. Loonperiode */}
        <KpiCard
          icon={Briefcase}
          label="Loonperiode"
          value={`P${iso_week}/${iso_year}`} // Using week/year as a proxy for now as per instructions "Periode X/Y"
          sublabel="Verloning openen"
          type="neutral"
          to={`/loonperiode`}
        />

        {/* 6. Documenten */}
        <KpiCard
          icon={FileSignature}
          label="Documenten"
          value="Bekijken"
          sublabel="Contracten & documenten"
          type="neutral"
          to={`/contracten`}
        />

        {/* 7. Ziek vandaag (live verzuim) */}
        <KpiCard
          icon={Pill}
          label="Ziek vandaag"
          value={ziekCount == null ? '—' : ziekCount}
          sublabel={ziekCount == null ? 'Verzuim onbekend' : ziekCount === 0 ? 'Niemand ziek gemeld' : `${ziekCount} lopende ziekmelding${ziekCount > 1 ? 'en' : ''}`}
          type={ziekCount > 0 ? 'attention' : 'neutral'}
          to={`/medewerkers`}
        />

        {/* 8. Te laat geklokt (live vandaag) */}
        <KpiCard
          icon={Clock}
          label="Te laat geklokt"
          value={teLaat == null ? '—' : teLaat.count}
          sublabel={teLaat == null ? 'Onbekend' : teLaat.count === 0 ? 'Iedereen op tijd vandaag' : `${teLaat.count} na geplande starttijd (>${teLaat.grace_minutes}m)`}
          type={teLaat && teLaat.count > 0 ? 'attention' : 'neutral'}
          to={`/inklokken`}
        />

        {/* 9. Uren-guard (out-of-band, deze week) */}
        <KpiCard
          icon={Activity}
          label="Buiten bandbreedte"
          value={guardCount == null ? '—' : guardCount}
          sublabel={guardCount == null ? 'Guard uit / onbekend' : guardCount === 0 ? 'Iedereen binnen contracturen' : `${guardCount} medewerker${guardCount > 1 ? 's' : ''} onder/overbezet`}
          type={guardCount > 0 ? 'attention' : 'neutral'}
          to={`/weekoverzicht`}
        />

        {/* 10. Vacatures (Placeholder) */}
        <KpiCard
          icon={BriefcaseBusiness}
          label="Vacatures"
          value="—"
          sublabel="Binnenkort beschikbaar"
          type="neutral"
        />
      </div>
    </div>
  );
}
